/* ===========================================================
   Per-page SEO — titles, meta description, canonical, Open Graph,
   robots directive and JSON-LD structured data.

   All copy is B2B / trade-focused by design: the words "wholesale",
   "trade", "food service" and "catering" signal a trade supplier so
   consumer searches (weddings, single cakes) self-filter out.

   Indexing is controlled centrally by SEO_INDEX — while false, every
   page emits <meta name="robots" content="noindex,nofollow">.
   =========================================================== */

import { Helmet } from 'react-helmet-async';
import {
  SITE_URL,
  SITE_NAME,
  SITE_OG_IMAGE,
  SITE_DEFAULT_DESCRIPTION,
  SEO_INDEX,
  COMPANY,
  canonicalUrl,
} from '@/config/site';

export interface SeoProps {
  /** Page title WITHOUT the site-name suffix (added automatically). */
  title: string;
  description?: string;
  /** Route path for the canonical URL, e.g. "/about" or "/product/ABC". */
  path: string;
  /** Absolute image URL for social sharing. Falls back to the brand image. */
  image?: string;
  /** og:type — "website" for most pages, "article" where relevant. */
  type?: 'website' | 'article' | 'product';
  /** Extra JSON-LD blocks to inject (Product, BreadcrumbList, etc.). */
  jsonLd?: Array<Record<string, unknown>>;
}

/** Organization schema — emitted on every page. Signals a trade supplier. */
function organizationLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    description: 'Premium wholesale patisserie supplier for the UK food service industry',
    url: SITE_URL,
    logo: `${SITE_URL}/assets/didiers-logo.svg`,
    image: SITE_OG_IMAGE,
    telephone: COMPANY.telephone,
    email: COMPANY.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY.streetAddress,
      addressLocality: COMPANY.addressLocality,
      postalCode: COMPANY.postalCode,
      addressCountry: COMPANY.addressCountry,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: COMPANY.telephone,
      email: COMPANY.email,
      areaServed: 'GB',
      availableLanguage: 'English',
    },
  };
}

export function Seo({ title, description, path, image, type = 'website', jsonLd = [] }: SeoProps) {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const desc = description ?? SITE_DEFAULT_DESCRIPTION;
  const canonical = canonicalUrl(path);
  const ogImage = image ?? SITE_OG_IMAGE;

  const blocks = [organizationLd(), ...jsonLd];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      {SEO_INDEX ? (
        <meta name="robots" content="index,follow" />
      ) : (
        <meta name="robots" content="noindex,nofollow" />
      )}

      {/* Open Graph — primarily for LinkedIn / email link previews (B2B channels). */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/** Helper: build a BreadcrumbList JSON-LD block from [label, path] pairs. */
export function breadcrumbLd(items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: canonicalUrl(it.path),
    })),
  };
}
